import { useMemo } from "react";

import { useGetCountries } from "@/api/country/useCountryAPI";
import { useGetSpatialAggregationTypes } from "@/api/dataset/useDatasetAPI";
import Dropdown from "@/components/ui/dropdown";
import SearchBox from "@/components/ui/searchBox";
import {
  COUNTRY_DATA,
  COUNTRY_NAME,
  SPATIAL_AGGREGATION_NAME
} from "@/constants/dataset";
import { CountryCode } from "@/types/common";
import { GetDatasetListPayload } from "@/types/dataset";

const TEST_ID_PREFIX = "dataset-management-page-filter-section";

interface FilterSectionProps {
  filters: {
    selectedCountry: GetDatasetListPayload["country"] | "all";
    spatialAggregation: GetDatasetListPayload["spatial_aggregation_type"];
    published: GetDatasetListPayload["published"] | "all";
  };
  setFilters: (filters: {
    selectedCountry: GetDatasetListPayload["country"] | "all";
    spatialAggregation: GetDatasetListPayload["spatial_aggregation_type"];
    published: GetDatasetListPayload["published"] | "all";
  }) => void;
  searchQuery: string;
  setSearchQuery: (searchQuery: string) => void;
  disabled: boolean;
}

export default function FilterSection({
  filters,
  setFilters,
  searchQuery,
  setSearchQuery,
  disabled
}: FilterSectionProps) {
  const { data: countryData, isLoading: isLoadingCountryData } =
    useGetCountries();

  const { data: spatialAggData, isLoading: isLoadingSpatialAggData } =
    useGetSpatialAggregationTypes();

  const spatialAggregationOptions = useMemo(
    () =>
      spatialAggData
        ? [
            { label: "All", value: "all" },
            { label: "None", value: "none" },
            ...spatialAggData.map((type) => ({
              label:
                type.code === "poi"
                  ? SPATIAL_AGGREGATION_NAME[type.code]
                  : type.name,
              value: type.code
            }))
          ]
        : [],
    [spatialAggData]
  );

  return (
    <div className="flex gap-4">
      <div className="w-[320px]">
        <SearchBox
          inputFieldName={`${TEST_ID_PREFIX}-search-dataset`}
          disabled={disabled}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          placeHolder="Search datasets..."
          className="max-w-sm"
          testId={TEST_ID_PREFIX}
        />
      </div>

      <div className="flex gap-2 ml-auto">
        <div className="w-[180px]">
          <Dropdown
            name="country"
            disabled={disabled}
            placeholder="Country"
            data={
              countryData?.map((country) => {
                if (country.name.toLowerCase() === "global")
                  return {
                    label: COUNTRY_NAME["all"],
                    value: "all"
                  };
                return {
                  label: country.name,
                  value: country.id
                };
              }) ?? COUNTRY_DATA
            }
            isLoading={isLoadingCountryData}
            value={
              filters.selectedCountry
                ? {
                    label: COUNTRY_NAME[filters.selectedCountry],
                    value: filters.selectedCountry
                  }
                : {
                    label: COUNTRY_NAME["all"],
                    value: "all"
                  }
            }
            onSelect={(data) => {
              if (data) {
                setFilters({
                  ...filters,
                  selectedCountry: data.value as CountryCode
                });
              }
            }}
          />
        </div>

        <div className="w-[180px]">
          <Dropdown
            name="spatialAggregation"
            disabled={disabled}
            placeholder="Spatial Aggregation"
            data={spatialAggregationOptions}
            isLoading={isLoadingSpatialAggData}
            value={
              filters.spatialAggregation
                ? {
                    label:
                      SPATIAL_AGGREGATION_NAME[
                        filters.spatialAggregation as keyof typeof SPATIAL_AGGREGATION_NAME
                      ],

                    value: filters.spatialAggregation
                  }
                : null
            }
            onSelect={(data) => {
              if (data) {
                setFilters({
                  ...filters,
                  spatialAggregation: data.value as
                    | "all"
                    | "poi"
                    | "polygon"
                    | "adm_area"
                });
              }
            }}
          />
        </div>

        <div className="w-[180px]">
          <Dropdown
            name="published"
            disabled={disabled}
            placeholder="Status"
            data={[
              { label: "All", value: "all" },
              { label: "Published", value: "true" },
              { label: "Unpublished", value: "false" }
            ]}
            value={
              filters.published !== "all"
                ? {
                    label: filters.published ? "Published" : "Unpublished",
                    value: filters.published ? "true" : "false"
                  }
                : { label: "All", value: "all" }
            }
            onSelect={(data) => {
              if (data) {
                setFilters({
                  ...filters,
                  published:
                    data.value === "all"
                      ? "all"
                      : data.value === "true"
                        ? true
                        : false
                });
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
